import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { teacherNav } from './navConfig';
import LessonModal from './LessonModal';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

function todayLabel(): string {
  return new Date().toLocaleDateString('tr-TR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

interface NotificationItem {
  id: string;
  icon: string;
  tone: string;
  text: string;
  path: string;
}

export default function PanelLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lessonModal, setLessonModal] = useState(false);
  const [lessonDefaultGroupId, setLessonDefaultGroupId] = useState<string | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const navigate = useNavigate();
  const { signOut, profile } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/giris', { replace: true });
    }
  };

  useEffect(() => {
    const open = (e: Event) => {
      setLessonDefaultGroupId((e as CustomEvent).detail?.groupId ?? null);
      setLessonModal(true);
    };
    window.addEventListener('zehrahoca:open-lesson-modal', open);
    return () => window.removeEventListener('zehrahoca:open-lesson-modal', open);
  }, []);

  // Bildirim verilerini (bugünkü ders / bekleyen ödev / bitmek üzere paket) çek
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const today = getTodayStr();
        const [{ data: lessons }, { data: assignments }, { data: packages }] = await Promise.all([
          supabase.from('zehrahoca_lessons').select('id').eq('date', today),
          supabase.from('zehrahoca_assignments').select('id').in('status', ['gonderildi', 'goruldu']),
          supabase.from('zehrahoca_packages').select('id, remaining_sessions').gt('remaining_sessions', 0).lte('remaining_sessions', 2),
        ]);
        if (!active) return;

        const items: NotificationItem[] = [];
        const todayCount = (lessons ?? []).length;
        if (todayCount > 0) {
          items.push({
            id: 'lessons',
            icon: 'ri-calendar-check-line',
            tone: 'bg-primary-100 text-primary-700',
            text: `Bugün ${todayCount} dersiniz var`,
            path: '/panel/program',
          });
        }
        const pendingCount = (assignments ?? []).length;
        if (pendingCount > 0) {
          items.push({
            id: 'assignments',
            icon: 'ri-task-line',
            tone: 'bg-amber-100 text-amber-700',
            text: `${pendingCount} bekleyen ödev teslimi var`,
            path: '/panel/odevler',
          });
        }
        const endingCount = (packages ?? []).length;
        if (endingCount > 0) {
          items.push({
            id: 'packages',
            icon: 'ri-alarm-warning-line',
            tone: 'bg-red-100 text-red-600',
            text: `${endingCount} paket bitmek üzere`,
            path: '/panel/odeme',
          });
        }
        setNotifications(items);
      } catch {
        // Bildirim çekilemezse sessizce geç; üst bar çalışmaya devam etsin.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const openLessonModal = (groupId: string | null = null) => {
    setLessonDefaultGroupId(groupId);
    setLessonModal(true);
  };

  const sidebar = (
    <div className="flex h-full flex-col bg-gradient-to-b from-primary-950 to-primary-900">
      <div className="flex items-center gap-3 px-5 py-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 font-heading text-xl font-extrabold text-primary-950">
          Z
        </div>
        <div className="leading-tight">
          <p className="font-heading text-[15px] font-bold text-white">Zehra Hocayla</p>
          <p className="text-xs font-medium text-accent-300">Matematik</p>
        </div>
      </div>

      <div className="px-5 pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-primary-300">Öğretmen Paneli</p>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto scrollbar-thin px-3 py-2">
        {teacherNav.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                isActive
                  ? 'bg-white/10 text-white ring-1 ring-accent-500/30'
                  : 'text-primary-200 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${
                    isActive ? 'bg-accent-500 text-primary-950' : 'bg-white/5 text-primary-300 group-hover:text-white'
                  }`}
                >
                  <i className={`${item.icon} text-base`} />
                </span>
                <span className="whitespace-nowrap">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-4">
        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-primary-200 transition hover:bg-white/5 hover:text-white cursor-pointer"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white/5 text-primary-300">
            <i className="ri-logout-box-r-line text-base" />
          </span>
          <span className="whitespace-nowrap">Çıkış Yap</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-50">
      {/* Mobil çizim */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 w-72">{sidebar}</div>
        </div>
      )}

      {/* Masaüstü sabit menü */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block print:hidden">{sidebar}</aside>

      <div className="lg:pl-64 print:pl-0">
        {/* Üst bar */}
        <header className="sticky top-0 z-20 border-b border-background-200 bg-background-50/90 backdrop-blur print:hidden">
          <div className="flex items-center justify-between gap-4 px-4 py-3.5 md:px-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-700 transition hover:bg-background-100 lg:hidden cursor-pointer"
                aria-label="Menüyü aç"
              >
                <i className="ri-menu-3-line text-xl" />
              </button>
              <div>
                <p className="hidden text-xs font-medium text-foreground-500 sm:block">Bugün</p>
                <p className="font-heading text-sm font-semibold capitalize text-foreground-950">{todayLabel()}</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => openLessonModal()}
                className="hidden items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 sm:flex cursor-pointer"
              >
                <i className="ri-add-line text-base" />
                Yeni Ders Ekle
              </button>
              <button
                type="button"
                onClick={() => navigate('/panel/odevler')}
                className="hidden items-center gap-2 whitespace-nowrap rounded-lg bg-accent-500 px-4 py-2.5 text-sm font-semibold text-primary-950 transition hover:bg-accent-600 md:flex cursor-pointer"
              >
                <i className="ri-task-line text-base" />
                Yeni Ödev Gönder
              </button>

              {/* Bildirim zili */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setNotifOpen((v) => !v); setProfileOpen(false); }}
                  className="relative flex h-10 w-10 items-center justify-center rounded-lg text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                  aria-label="Bildirimler"
                >
                  <i className="ri-notification-3-line text-xl" />
                  {notifications.length > 0 && (
                    <span className="absolute right-2 top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-primary-950 ring-2 ring-background-50">
                      {notifications.length}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setNotifOpen(false)} aria-hidden="true" />
                    <div className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-xl border border-background-200 bg-white shadow-lg">
                      <div className="border-b border-background-200 px-4 py-3">
                        <p className="text-sm font-semibold text-foreground-950">Bildirimler</p>
                      </div>
                      <div className="max-h-80 overflow-y-auto py-1">
                        {notifications.length === 0 ? (
                          <p className="px-4 py-6 text-center text-sm text-foreground-500">
                            Yeni bildirim bulunmuyor.
                          </p>
                        ) : (
                          notifications.map((n) => (
                            <button
                              key={n.id}
                              type="button"
                              onClick={() => { setNotifOpen(false); navigate(n.path); }}
                              className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-background-50 cursor-pointer"
                            >
                              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${n.tone}`}>
                                <i className={`${n.icon} text-base`} />
                              </span>
                              <span className="text-sm text-foreground-700">{n.text}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Profil menüsü */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setProfileOpen((v) => !v); setNotifOpen(false); }}
                  className="flex items-center gap-2.5 rounded-lg py-1.5 pl-1.5 pr-2 transition hover:bg-background-100 cursor-pointer"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-white">
                    Z
                  </div>
                  <div className="hidden text-left leading-tight md:block">
                    <p className="text-sm font-semibold text-foreground-950">{profile?.full_name ?? 'Zehra Hoca'}</p>
                    <p className="text-xs text-foreground-500">Yönetici</p>
                  </div>
                  <i className="ri-arrow-down-s-line text-sm text-foreground-500" />
                </button>

                {profileOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setProfileOpen(false)} aria-hidden="true" />
                    <div className="absolute right-0 z-40 mt-2 w-56 overflow-hidden rounded-xl border border-background-200 bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        onClick={() => { setProfileOpen(false); navigate('/panel/ayarlar?tab=profil'); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 transition hover:bg-background-50 cursor-pointer"
                      >
                        <i className="ri-user-3-line text-base text-foreground-500" /> Profil Ayarları
                      </button>
                      <button
                        type="button"
                        onClick={() => { setProfileOpen(false); navigate('/panel/ayarlar?tab=sifre'); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 transition hover:bg-background-50 cursor-pointer"
                      >
                        <i className="ri-lock-password-line text-base text-foreground-500" /> Şifre Değiştir
                      </button>
                      <div className="my-1 border-t border-background-200" />
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-50 cursor-pointer"
                      >
                        <i className="ri-logout-box-r-line text-base" /> Çıkış Yap
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 py-6 md:px-6 md:py-8 print:p-0">
          <Outlet />
        </main>
      </div>

      <LessonModal open={lessonModal} onClose={() => setLessonModal(false)} defaultGroupId={lessonDefaultGroupId} />
    </div>
  );
}